import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Container, Paper, Typography, TextField, Button, Divider,
  Alert, CircularProgress, InputAdornment, Link, Skeleton, Tabs, Tab,
  IconButton
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import GoogleIcon from '@mui/icons-material/Google';
import SendIcon from '@mui/icons-material/Send';
import LockIcon from '@mui/icons-material/Lock';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

// LINE Icon component
const LineIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.349 0 .63.285.63.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.105.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"/>
  </svg>
);

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    loginWithPhone,
    loginWithPassword,
    registerWithPassword,
    sendPhoneCode,
    loginWithGoogle,
    getLineLoginUrl,
    isAuthenticated
  } = useAuth();

  // 登入模式：0=密碼登入, 1=簡訊登入
  const [loginMode, setLoginMode] = useState(0);
  const [isRegisterMode, setIsRegisterMode] = useState(false);

  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [verificationCode, setVerificationCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [devCode, setDevCode] = useState('');

  // 登入方式設定
  const [authConfig, setAuthConfig] = useState({
    phone_enabled: true,
    google_enabled: false,
    google_client_id: '',
    line_enabled: false,
    line_channel_id: ''
  });
  const [configLoading, setConfigLoading] = useState(true);

  // 載入登入方式設定
  useEffect(() => {
    const loadAuthConfig = async () => {
      try {
        const res = await api.get('/settings/auth/public');
        if (res.data.success) {
          setAuthConfig(res.data.config);
        }
      } catch (err) {
        console.error('載入登入設定失敗:', err);
      } finally {
        setConfigLoading(false);
      }
    };
    loadAuthConfig();
  }, []);

  // 如果已登入，重定向
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  // 驗證碼倒數計時
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // 發送驗證碼
  const handleSendCode = async () => {
    if (!phone || !/^09\d{8}$/.test(phone)) {
      setError('請輸入正確的手機號碼（09 開頭，共 10 碼）');
      return;
    }

    setLoading(true);
    setError('');

    const result = await sendPhoneCode(phone);

    if (result.success) {
      setCodeSent(true);
      setCountdown(60);
      if (result.devCode) {
        setDevCode(result.devCode);
      }
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  // 密碼登入
  const handlePasswordLogin = async () => {
    if (!phone || !/^09\d{8}$/.test(phone)) {
      setError('請輸入正確的手機號碼（09 開頭，共 10 碼）');
      return;
    }

    if (!password || password.length < 6) {
      setError('密碼長度至少 6 個字元');
      return;
    }

    setLoading(true);
    setError('');

    const result = await loginWithPassword(phone, password);

    if (result.success) {
      // GA4 login event
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'login',
        method: 'password'
      });

      // Facebook Pixel
      if (typeof window.fbq === 'function') {
        window.fbq('trackCustom', 'Login', { method: 'password' });
      }

      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  // 註冊
  const handleRegister = async () => {
    if (!phone || !/^09\d{8}$/.test(phone)) {
      setError('請輸入正確的手機號碼（09 開頭，共 10 碼）');
      return;
    }

    if (!password || password.length < 6) {
      setError('密碼長度至少 6 個字元');
      return;
    }

    setLoading(true);
    setError('');

    const result = await registerWithPassword(phone, password, name);

    if (result.success) {
      // GA4 sign_up event
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'sign_up',
        method: 'password'
      });

      // Facebook Pixel CompleteRegistration
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'CompleteRegistration', { method: 'password' });
      }

      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  // 手機登入 (簡訊驗證碼)
  const handlePhoneLogin = async () => {
    if (!verificationCode || verificationCode.length !== 4) {
      setError('請輸入 4 位數驗證碼');
      return;
    }

    setLoading(true);
    setError('');

    const result = await loginWithPhone(phone, verificationCode);

    if (result.success) {
      // GA4 login event
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'login',
        method: 'phone_sms'
      });

      // Facebook Pixel
      if (typeof window.fbq === 'function') {
        window.fbq('trackCustom', 'Login', { method: 'phone_sms' });
      }

      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  // Google 登入
  const handleGoogleLogin = async () => {
    if (!authConfig.google_enabled) {
      setError('Google 登入尚未啟用');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // 使用 Google Identity Services
      if (window.google?.accounts?.id && authConfig.google_client_id) {
        window.google.accounts.id.initialize({
          client_id: authConfig.google_client_id,
          callback: async (response) => {
            const result = await loginWithGoogle(response.credential);
            if (result.success) {
              // GA4 login event
              window.dataLayer = window.dataLayer || [];
              window.dataLayer.push({
                event: 'login',
                method: 'google'
              });

              // Facebook Pixel
              if (typeof window.fbq === 'function') {
                window.fbq('trackCustom', 'Login', { method: 'google' });
              }

              const from = location.state?.from?.pathname || '/';
              navigate(from, { replace: true });
            } else {
              setError(result.error);
            }
            setLoading(false);
          }
        });
        window.google.accounts.id.prompt();
      } else {
        // 開發環境模擬
        const mockUser = {
          id: Date.now(),
          name: 'Google 測試用戶',
          email: 'test@gmail.com',
          avatar: null,
          provider: 'google',
          createdAt: new Date().toISOString()
        };
        localStorage.setItem('authToken', 'dev-token-google');
        localStorage.setItem('user', JSON.stringify(mockUser));
        window.location.reload();
      }
    } catch (err) {
      setError('Google 登入失敗，請稍後再試');
      setLoading(false);
    }
  };

  // LINE 登入
  const handleLineLogin = () => {
    if (!authConfig.line_enabled) {
      setError('LINE 登入尚未啟用');
      return;
    }

    // GA4 login_attempt event (before redirect)
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'login_attempt',
      method: 'line'
    });

    const lineUrl = getLineLoginUrl(authConfig.line_channel_id);
    window.location.href = lineUrl;
  };

  // 檢查是否有任何社群登入啟用
  const hasSocialLogin = authConfig.google_enabled || authConfig.line_enabled;

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Paper sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            會員登入
          </Typography>
          <Typography variant="body2" color="text.secondary">
            登入後享有完整購物體驗
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* 社群登入按鈕 */}
        {configLoading ? (
          <Box sx={{ mb: 3 }}>
            <Skeleton variant="rectangular" height={48} sx={{ mb: 2, borderRadius: 1 }} />
            <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 1 }} />
          </Box>
        ) : hasSocialLogin && (
          <Box sx={{ mb: 3 }}>
            {authConfig.google_enabled && (
              <Button
                fullWidth
                variant="outlined"
                size="large"
                startIcon={<GoogleIcon />}
                onClick={handleGoogleLogin}
                disabled={loading}
                sx={{
                  mb: authConfig.line_enabled ? 2 : 0,
                  borderColor: '#4285f4',
                  color: '#4285f4',
                  '&:hover': { borderColor: '#357ae8', bgcolor: '#f5f9ff' }
                }}
              >
                使用 Google 帳號登入
              </Button>
            )}

            {authConfig.line_enabled && (
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={<LineIcon />}
                onClick={handleLineLogin}
                disabled={loading}
                sx={{
                  bgcolor: '#00B900',
                  '&:hover': { bgcolor: '#00a000' }
                }}
              >
                使用 LINE 帳號登入
              </Button>
            )}
          </Box>
        )}

        {hasSocialLogin && authConfig.phone_enabled && (
          <Divider sx={{ my: 3 }}>
            <Typography variant="body2" color="text.secondary">或使用手機號碼</Typography>
          </Divider>
        )}

        {/* 手機登入 */}
        {authConfig.phone_enabled && (
          <Box>
            {/* 登入方式切換 */}
            <Tabs
              value={loginMode}
              onChange={(e, v) => {
                setLoginMode(v);
                setError('');
                setCodeSent(false);
              }}
              sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="密碼登入" icon={<LockIcon />} iconPosition="start" />
              <Tab label="簡訊登入" icon={<SendIcon />} iconPosition="start" />
            </Tabs>

            {/* 密碼登入 */}
            {loginMode === 0 && (
              <Box>
                <TextField
                  fullWidth
                  label="手機號碼"
                  placeholder="09xxxxxxxx"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon color="action" />
                      </InputAdornment>
                    )
                  }}
                  sx={{ mb: 2 }}
                />

                {isRegisterMode && (
                  <TextField
                    fullWidth
                    label="姓名（選填）"
                    placeholder="請輸入您的姓名"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                )}

                <TextField
                  fullWidth
                  label="密碼"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="請輸入密碼（至少 6 個字元）"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  sx={{ mb: 2 }}
                />

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={isRegisterMode ? handleRegister : handlePasswordLogin}
                  disabled={loading || !phone || !password}
                  sx={{ mb: 1 }}
                >
                  {loading ? <CircularProgress size={24} /> : (isRegisterMode ? '註冊' : '登入')}
                </Button>

                <Box sx={{ textAlign: 'center' }}>
                  <Button
                    size="small"
                    onClick={() => {
                      setIsRegisterMode(!isRegisterMode);
                      setError('');
                    }}
                  >
                    {isRegisterMode ? '已有帳號？立即登入' : '還沒有帳號？立即註冊'}
                  </Button>
                </Box>
              </Box>
            )}

            {/* 簡訊登入 */}
            {loginMode === 1 && (
              <Box>
                <TextField
                  fullWidth
                  label="手機號碼"
                  placeholder="09xxxxxxxx"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  disabled={codeSent && countdown > 0}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon color="action" />
                      </InputAdornment>
                    )
                  }}
                  sx={{ mb: 2 }}
                />

                {!codeSent ? (
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={handleSendCode}
                    disabled={loading || !phone}
                    startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                  >
                    {loading ? '發送中...' : '發送驗證碼'}
                  </Button>
                ) : (
                  <Box>
                    <TextField
                      fullWidth
                      label="驗證碼"
                      placeholder="請輸入 4 位數驗證碼"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                      sx={{ mb: 2 }}
                      inputProps={{
                        autoComplete: 'one-time-code',
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                        maxLength: 4,
                      }}
                      InputProps={{
                        endAdornment: countdown > 0 && (
                          <InputAdornment position="end">
                            <Typography variant="body2" color="text.secondary">
                              {countdown}s
                            </Typography>
                          </InputAdornment>
                        )
                      }}
                    />

                    {devCode && (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        [開發環境] 驗證碼：{devCode}
                      </Alert>
                    )}

                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Button
                        variant="outlined"
                        size="large"
                        onClick={handleSendCode}
                        disabled={countdown > 0 || loading}
                        sx={{ flex: 1 }}
                      >
                        {countdown > 0 ? `重新發送 (${countdown}s)` : '重新發送'}
                      </Button>
                      <Button
                        variant="contained"
                        size="large"
                        onClick={handlePhoneLogin}
                        disabled={loading || verificationCode.length !== 4}
                        sx={{ flex: 1 }}
                      >
                        {loading ? <CircularProgress size={24} /> : '登入'}
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        {/* 條款說明 */}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
          登入即表示您同意我們的{' '}
          <Link href="/shopping-guide" underline="hover">服務條款</Link>
          {' '}與{' '}
          <Link href="/shopping-guide" underline="hover">隱私權政策</Link>
        </Typography>
      </Paper>

      {/* 購物說明連結 */}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          第一次購物？查看{' '}
          <Link href="/shopping-guide" underline="hover" fontWeight="bold">
            購物說明
          </Link>
        </Typography>
      </Box>
    </Container>
  );
};

export default Login;
